import {
  useCallback,
  useRef,
  useState,
  type CSSProperties,
  type SetStateAction,
  type Dispatch,
} from 'react';

import Animations from '@dynatrace/strato-design-tokens/animations';

import { usePrevious } from '../../../core/hooks/usePrevious.js';

type UseAnimatedPagePanelsProps = {
  setLayoutStyles: Dispatch<SetStateAction<CSSProperties | undefined>>;
};

/** Hook providing a function to animate conditionally rendered panels from one state to another. */
export function useAnimatedPagePanels({
  setLayoutStyles,
}: UseAnimatedPagePanelsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gridTemplateAreasFinal, setGridTemplateAreasFinal] = useState('');
  const [gridTemplateColumnsFinal, setGridTemplateColumnsFinal] = useState('');
  const gridTemplateAreasPrev = usePrevious(gridTemplateAreasFinal);
  const gridTemplateColumnsPrev = usePrevious(gridTemplateColumnsFinal);
  const transitionRunningRef = useRef(false);

  // Callback to set the final grid styles after the transition.
  const handleTransition = useCallback(
    (event: TransitionEvent) => {
      // Ignore other bubbling transition event.
      if (
        event.propertyName !== 'grid-template-columns' ||
        event.target !== containerRef.current
      ) {
        return;
      }

      setLayoutStyles({
        gridTemplateAreas: gridTemplateAreasFinal,
        gridTemplateColumns: gridTemplateColumnsFinal,
      });
      // Remove transition from container to not animate a resize handle being dragged.
      containerRef.current && (containerRef.current.style.transition = '');
      transitionRunningRef.current = false;
    },
    [gridTemplateAreasFinal, gridTemplateColumnsFinal, setLayoutStyles],
  );

  // Using a 'setTimeout' doesn't reliably update the styles at the time we need them to be updated.
  // The only way to do so is to force the browser to run a layout step.
  const forceLayout = () => {
    // Force layout to flush styles. Else, we would still jump directly to the start keyframe
    // of the new transition instead of canceling the old one.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-unused-expressions
    containerRef.current?.offsetHeight;
  };

  // Use a callback ref for the roving focus hook container to be able to react
  // to updates changing references and conditionally rendered containers.
  const setRef = useCallback(
    (node: HTMLDivElement) => {
      const container = containerRef.current;
      // Clean up event listeners of previous container
      if (container) {
        container.removeEventListener('transitionend', handleTransition);
      }

      // Initialize event listeners for roving focus container and children.
      if (node) {
        node.addEventListener('transitionend', handleTransition);
      }

      containerRef.current = node;
    },
    [handleTransition],
  );

  const animatePagePanels = useCallback(
    (
      gridTemplateAreas: string,
      gridTemplateAreasStart: string,
      gridTemplateColumns: string,
      gridTemplateColumnsStart: string,
      gridTemplateColumnsEnd: string,
      addedPanels = 0,
      animationsDisabled = false,
    ) => {
      if (!containerRef.current) {
        return;
      }

      if (animationsDisabled) {
        setLayoutStyles({
          gridTemplateAreas: gridTemplateAreasStart,
          gridTemplateColumns: gridTemplateColumnsStart,
        });

        forceLayout();
        setLayoutStyles({
          gridTemplateAreas,
          gridTemplateColumns,
        });
        return;
      }

      setGridTemplateAreasFinal(gridTemplateAreas);
      setGridTemplateColumnsFinal(gridTemplateColumns);

      let animationDuration = Animations.Move.Duration.Fast;
      let animationEasing = Animations.SlideOut.Easing;
      if (addedPanels > 0) {
        animationDuration = Animations.SlideIn.Duration.Fast;
        animationEasing = Animations.SlideIn.Easing;
      }

      if (!transitionRunningRef.current) {
        // Temporarily apply transition to animate conditionally rendered panels.
        // Else, dragging the resize handles would also constantly trigger transitions.
        containerRef.current.style.transition = `grid-template-columns ${animationDuration} ${animationEasing}`;
        setLayoutStyles({
          gridTemplateAreas: gridTemplateAreasStart,
          gridTemplateColumns: gridTemplateColumnsStart,
        });
      } else {
        // If a transition is already in progress, we need to first jump to the final keyframe of the
        // transition and only then set the new starting styles to correctly start the new transition.
        // Else, it may appear like an added panel is animated from the wrong side if a panel on the
        // opposite side has just been removed but is not yet done transitioning.
        // Unfortunately, this doesn't work with 'setLayoutStyles' due to react batching the state updates.
        // Hence, we have to update the node's styles directly.

        // Temporarily disable transition to jump directly to the end of the
        // previous transition.
        containerRef.current.style.transition = '';
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        containerRef.current.style.gridTemplateAreas = gridTemplateAreasPrev!;
        containerRef.current.style.gridTemplateColumns =
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          gridTemplateColumnsPrev!;

        forceLayout();

        containerRef.current.style.gridTemplateAreas = gridTemplateAreasStart;
        containerRef.current.style.gridTemplateColumns =
          gridTemplateColumnsStart;

        forceLayout();
        // Temporarily apply transition to animate conditionally rendered panels.
        // Else, dragging the resize handles would also constantly trigger transitions.
        containerRef.current.style.transition = `grid-template-columns ${animationDuration} ${animationEasing}`;
      }

      // Defer setting the target columns to the next tick so the transition is triggered.
      requestAnimationFrame(() => {
        transitionRunningRef.current = true;
        // Set layout styles to transition to.
        setLayoutStyles({
          gridTemplateAreas: gridTemplateAreasStart,
          gridTemplateColumns: gridTemplateColumnsEnd,
        });
      });
    },
    // parentPanelRef and setLayoutStyles will never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridTemplateAreasPrev, gridTemplateColumnsPrev],
  );

  return { containerRef: setRef, animatePagePanels };
}
